export const WORKFORCE_ROLES = [
  'ADMIN',
  'SUPER_ADMIN',
  'BRANCH_SUPER_ADMIN',
  'OPERATIONS_MANAGER',
  'MANAGER',
  'SUPERVISOR',
  'FIELD_CLEANER',
  'CLEANER',
  'EMPLOYEE',
  'STAFF',
  'SCHEDULER_DISPATCHER',
  'CONTRACTOR',
  'SALES',
  'FINANCE',
];

/** Roles that are not company workforce (clients / system). */
const NON_WORKFORCE_ROLES = [
  'CLIENT_CONTACT_APPROVER',
  'CLIENT_CONTACT',
  'READ_ONLY_AUDITOR',
  'SOFTWARE_ADMIN',
];

/**
 * Predefined GraphQL UserRole keys used by Workforce (not clients / system).
 * Labels match backend UserRole enum values so Team Settings and Workforce agree.
 */
export const WORKFORCE_PREDEFINED_ROLES = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'BRANCH_SUPER_ADMIN', label: 'Branch Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'OPERATIONS_MANAGER', label: 'Operations Manager' },
  { value: 'SUPERVISOR', label: 'Supervisor' },
  { value: 'SCHEDULER_DISPATCHER', label: 'Scheduler/Dispatcher' },
  { value: 'FIELD_CLEANER', label: 'Field Cleaner' },
  { value: 'SALES', label: 'Sales' },
  { value: 'FINANCE', label: 'Finance' },
];

const PREDEFINED_ROLE_KEYS = new Set(
  WORKFORCE_PREDEFINED_ROLES.map((role) => role.value),
);

const WORKFORCE_ROLE_ALIASES = {
  MANAGER: 'OPERATIONS_MANAGER',
  CLEANER: 'FIELD_CLEANER',
  EMPLOYEE: 'FIELD_CLEANER',
  STAFF: 'FIELD_CLEANER',
  CONTRACTOR: 'SCHEDULER_DISPATCHER',
};

export const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');

export const hasAnyRole = (roles = [], expectedRoles = WORKFORCE_ROLES) => {
  const normalizedExpected = expectedRoles.map(normalizeRole);
  return (roles || []).some((role) =>
    normalizedExpected.includes(normalizeRole(role)),
  );
};

export const isWorkforcePerson = (user) => {
  const all = [...(user?.roles || []), user?.role];
  if (hasAnyRole(all, WORKFORCE_ROLES)) return true;
  if (hasAnyRole(all, NON_WORKFORCE_ROLES)) return false;
  // Include employees with empty, custom, or unrecognised roles
  // (e.g. "Employee", "Staff", or only `role` filled).
  return true;
};

export const workforceRoleLabel = (roles = [], singularRole = '') => {
  const all = [...(roles || []), singularRole];
  if (hasAnyRole(all, ['ADMIN', 'SUPER_ADMIN', 'BRANCH_SUPER_ADMIN']))
    return 'Admin';
  if (hasAnyRole(all, ['OPERATIONS_MANAGER', 'MANAGER'])) return 'Manager';
  if (hasAnyRole(all, ['SUPERVISOR'])) return 'Supervisor';
  if (hasAnyRole(all, ['FIELD_CLEANER', 'CLEANER'])) return 'Cleaner';
  if (hasAnyRole(all, ['SCHEDULER_DISPATCHER', 'CONTRACTOR']))
    return 'Contractor';
  if (hasAnyRole(all, ['SALES'])) return 'Sales';
  if (hasAnyRole(all, ['FINANCE'])) return 'Finance';
  if (hasAnyRole(all, ['EMPLOYEE', 'STAFF'])) return 'Employee';
  return 'Staff';
};

/** Map stored DB/GraphQL role strings onto a predefined workforce enum key. */
export function toWorkforceRoleKey(roles = [], singularRole = '') {
  const all = [...(roles || []), singularRole].filter(Boolean);
  for (const role of all) {
    const normalized = normalizeRole(role);
    const mapped = WORKFORCE_ROLE_ALIASES[normalized] || normalized;
    if (PREDEFINED_ROLE_KEYS.has(mapped)) return mapped;
  }
  return 'ADMIN';
}

export function workforcePredefinedRoleLabel(roleKey) {
  const match = WORKFORCE_PREDEFINED_ROLES.find((role) => role.value === roleKey);
  return match?.label || workforceRoleLabel([roleKey]);
}

export async function fetchAllCompanyUsers(graphqlRequest, query) {
  const loadPages = async (q) => {
    const first = await graphqlRequest(q, { page: 1, limit: 100 });
    const page1 = Array.isArray(first?.getAllUser?.data)
      ? first.getAllUser.data
      : [];
    const totalPages = Math.max(1, Number(first?.getAllUser?.totalPages) || 1);
    if (totalPages <= 1) return page1;
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        graphqlRequest(q, { page: i + 2, limit: 100 }),
      ),
    );
    return [
      ...page1,
      ...rest.flatMap((r) =>
        Array.isArray(r?.getAllUser?.data) ? r.getAllUser.data : [],
      ),
    ];
  };

  try {
    return await loadPages(query);
  } catch (err) {
    const msg = String(err?.message || err || '');
    // Retry without singular `role` if that field is missing in this schema.
    if (/\brole\b/i.test(msg) && /(^|\n)\s*role\s*(\n|$)/.test(query)) {
      return loadPages(query.replace(/(^|\n)\s*role\s*(?=\n|$)/, '\n'));
    }
    throw err;
  }
}
